/**
 * The project map in the browser (Leaflet 1.9.4, bundled; docs/specs/portal/client-area.md §5, §11). It draws what
 * the domain gives it, block boundaries (`blockFeatures`) and the lines-remaining layer (`linesRemaining`), with the
 * brand colours and stroke widths read from the CSS variables at runtime, so tenant themes apply. It is drawn over
 * Satellite with labels, so its box carries `data-basemap="imagery"` and every line its thin dark edge (decision 654).
 *
 * The client portal map adds (parity C4): the hazards staff published to the client portal (`portalHazards`: the
 * pilot hazard pack's hazards in the pack's own colours, under the lines; the landholder properties when shared) and
 * the project's own aircraft and vehicles with their trails (`portalTracking`, drawn as the Operations Hub draws them,
 * with no tracker state). A staff map adds the public air traffic around the project with its trails (MB, 27 Oct
 * 2026; decision 765: staff only), drawn as Teslyn Field draws aircraft (`aircraftLayer.ts`) and credited, never
 * fitted to. Text reaches the map only as text nodes; a pin's icon is only an image inside the pack.
 *
 * The staff live map (decision 842, docs/specs/cloud/live-tracking.md) is this map too, with what Teslyn Field's Map
 * offers: the base map chosen (`base`; the box's `data-basemap` and the lines' colours follow it), the hazard pins
 * (`pins`, drawn as Teslyn Field draws them), the hazards over the lines (`hazardsOver`, Teslyn Field's order),
 * popups with each feature's identify rows (`identify`), one fit per project (`fitKey`: a layer change or a refresh
 * never moves the map), follow and fly to an aircraft (`follow`, `flyTo`; dragging stops following).
 */
import { useEffect, useRef, type ReactElement } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  baseMapKind,
  boundsOf,
  featurePoints,
  hazardMarkLabel,
  markedPlacemarkStyle,
  isSearchArea,
  type AircraftColours,
  type AircraftPosition,
  type BaseLayerId,
  type HazardPinRecord,
  type MapFeature,
  type PortalHazardLayer,
  type PortalHazardPlacemark,
  type TrailPoint,
} from '@teslyn/domain';
import { drawAircraft } from '../map/aircraftLayer.ts';
import { RouteOverlay, type RouteFetcher } from '../map/publicRoute.ts';
import { baseLayer, colourOf, elementCss, shapesOf, textNode } from '../map/leaflet.ts';
import { firePopup } from '../map/cloudFires.tsx';
import { drawHazardPins, identifyPopup } from '../map/hazardPins.ts';

export interface RemainingMapTracking {
  readonly aircraft: readonly AircraftPosition[];
  readonly trails: ReadonlyMap<string, readonly TrailPoint[]>;
  readonly colours: ReadonlyMap<string, AircraftColours>;
  /** The public traffic services' credits while their aircraft are drawn (a staff map; never a client's). */
  readonly credits?: readonly string[];
  /**
   * The take-off, flight time and landing under each own aircraft (a staff map, as Teslyn Field). The client portal
   * map passes false: its "Aircraft and vehicles" list under the map has the flight line (client-area.md §11.1).
   */
  readonly flightLines?: boolean;
}

export interface RemainingMapProps {
  /** Drawn bottom first: block boundaries, then lines remaining. */
  readonly features: readonly MapFeature[];
  /** Short text for screen readers; the progress table holds the same numbers. */
  readonly label: string;
  /** Hazards under the lines (the client portal map, when published and shown). */
  readonly hazards?: readonly PortalHazardLayer[];
  /**
   * Aircraft over everything: the project's own aircraft and vehicles (the client portal map), or the public air
   * traffic around the project (a staff map).
   */
  readonly tracking?: RemainingMapTracking | null;
  /**
   * Drawn over the features with a popup each, never fitted to: the fire layers of a staff map (decision 806; the
   * client portal passes none).
   */
  readonly overlay?: readonly MapFeature[];
  /** The overlay's sources' credits (each agency's own words, DEA's). */
  readonly credits?: readonly string[];
  /** The base map (Satellite with labels unless chosen). */
  readonly base?: BaseLayerId;
  /** Hazard pins over the lines (a staff map). */
  readonly pins?: readonly HazardPinRecord[];
  /** The hazards over the lines, as Teslyn Field draws them (the client portal map keeps them under). */
  readonly hazardsOver?: boolean;
  /** A popup with each feature's identify rows on a click (an interactive map, a region for screen readers). */
  readonly identify?: boolean;
  /** Fit once per key (a project's id) instead of each time the features change. */
  readonly fitKey?: string;
  /** The aircraft kept in view; dragging the map stops it (`onFollowStopped`). */
  readonly follow?: string | null;
  readonly onFollowStopped?: () => void;
  /** Fly to one aircraft, with a counter so the same aircraft can be flown to twice. */
  readonly flyTo?: { readonly id: string; readonly nonce: number } | null;
  /** More classes on the map's box (its height). */
  readonly className?: string;
  /** A click on a public aircraft asks its planned route (R160: a staff map only); none: no route. */
  readonly routeOf?: RouteFetcher;
}

const ll = (p: { lat: number; lng: number }): L.LatLngTuple => [p.lat, p.lng];

/** A hazard map's search area: shading under the lines and the rest of the hazards, never in the way (R228). */
const SEARCH_AREA_PANE = 'portal-search-area';

/** One hazard placemark as a Leaflet layer, in the pack's own colours (canvas for the many powerlines). */
function hazardLayer(
  p: PortalHazardPlacemark,
  renderer: L.Renderer,
  pane: string,
  /** The green of a hazard marked not present on this base map (R243). */
  green: string,
): L.Layer {
  const m = p.mark;
  const s = m ? markedPlacemarkStyle(p.style, green) : p.style;
  const words = m ? hazardMarkLabel({ marked_at: m.at, marked_by_name: m.by }) : '';
  const tip = textNode(
    [words, p.name, p.folder, p.description].filter((x) => x.trim() !== '').join('\n'),
    'map-tooltip--lines',
  );
  if (p.shape.type === 'point') {
    if (s.icon) {
      const px = Math.round(24 * (s.scale > 0 ? s.scale : 1));
      return L.marker(ll(p.shape.at), {
        icon: L.icon({
          iconUrl: s.icon,
          iconSize: [px, px],
          iconAnchor: [px / 2, px / 2],
          // a pin's own picture stays; a green ring says it is marked
          ...(m ? { className: 'map-hazard-not-present' } : {}),
        }),
        keyboard: false,
        alt: p.name,
        pane,
      }).bindTooltip(tip);
    }
    const dot = s.dot ?? s.line ?? colourOf('accent');
    return L.circleMarker(ll(p.shape.at), {
      radius: 5,
      color: dot,
      weight: 1,
      fillColor: dot,
      fillOpacity: 1,
      renderer,
      pane,
    }).bindTooltip(tip);
  }
  if (p.shape.type === 'line') {
    return L.polyline(p.shape.points.map(ll), {
      color: s.line ?? colourOf('accent'),
      weight: s.width,
      opacity: 1,
      renderer,
      pane,
    }).bindTooltip(tip, { sticky: true });
  }
  const shading = isSearchArea(p.folder, p.name);
  const polygon = L.polygon([p.shape.outer.map(ll), ...p.shape.holes.map((h) => h.map(ll))], {
    stroke: s.line !== null,
    color: s.line ?? 'transparent',
    weight: s.width,
    opacity: 1,
    fill: s.fill !== null,
    fillColor: s.fill ?? 'transparent',
    fillOpacity: m ? 0.25 : 1, // the pack's colour carries its own transparency
    ...(shading ? { interactive: false, pane: SEARCH_AREA_PANE } : { renderer, pane }),
  });
  return shading ? polygon : polygon.bindTooltip(tip, { sticky: true });
}

export function RemainingMap({
  features,
  label,
  hazards,
  tracking,
  overlay,
  credits,
  base = 'hybrid',
  pins,
  hazardsOver = false,
  identify = false,
  fitKey,
  follow = null,
  onFollowStopped,
  flyTo = null,
  className,
  routeOf,
}: RemainingMapProps): ReactElement {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const group = useRef<L.LayerGroup | null>(null);
  const layers = useRef<{
    base: L.LayerGroup | null;
    hazards: L.LayerGroup;
    pins: L.LayerGroup;
    trails: L.LayerGroup;
    aircraft: L.LayerGroup;
    route: RouteOverlay;
    hazardCanvas: L.Canvas;
  } | null>(null);
  const top = useRef<L.LayerGroup | null>(null);
  const fitted = useRef(false);
  // the fit key the map last fitted (a map with a key fits once per key, never on a layer change or a refresh)
  const fittedKey = useRef<string | null>(null);
  const latest = useRef({ follow, onFollowStopped, tracking, routeOf });
  latest.current = { follow, onFollowStopped, tracking, routeOf };

  useEffect(() => {
    if (!host.current) return undefined;
    // no zoom, fade or pan animation for people who ask for less motion
    const still =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const m = L.map(host.current, {
      zoomControl: true,
      attributionControl: true,
      zoomAnimation: !still,
      fadeAnimation: !still,
      markerZoomAnimation: !still,
    });
    // hazards under the survey's own lines (the client portal map) or over them (Teslyn Field's order); pins over
    // the lines; trails over them; aircraft on top (the marker pane)
    m.createPane('portal-hazards').style.zIndex = hazardsOver ? '410' : '350';
    const shading = m.createPane(SEARCH_AREA_PANE);
    shading.style.zIndex = '340';
    shading.style.pointerEvents = 'none';
    m.createPane('portal-pins').style.zIndex = '420';
    m.createPane('portal-trails').style.zIndex = '450';
    layers.current = {
      base: null,
      hazards: L.layerGroup().addTo(m),
      pins: L.layerGroup().addTo(m),
      trails: L.layerGroup().addTo(m),
      route: new RouteOverlay(m, L.layerGroup().addTo(m), () => -new Date().getTimezoneOffset() / 60),
      aircraft: L.layerGroup().addTo(m),
      hazardCanvas: L.canvas({ pane: 'portal-hazards', padding: 0.5 }),
    };
    group.current = L.layerGroup().addTo(m);
    top.current = L.layerGroup().addTo(m);
    map.current = m;
    fitted.current = false;
    fittedKey.current = null;
    // dragging the map stops following (Teslyn Field's follow mode)
    m.on('dragstart', () => {
      if (latest.current.follow) latest.current.onFollowStopped?.();
    });
    const resize =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => m.invalidateSize());
    resize?.observe(host.current);
    return () => {
      resize?.disconnect();
      m.remove();
      map.current = null;
      group.current = null;
      layers.current = null;
      top.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the panes are made once, with the map
  }, []);

  // ---- the base map (the Field "Satellite with labels" to start)
  useEffect(() => {
    const m = map.current;
    const l = layers.current;
    if (!m || !l) return;
    l.base?.remove();
    l.base = baseLayer(base).addTo(m);
    l.base.eachLayer((t) => (t as L.TileLayer).bringToBack());
  }, [base]);

  // ---- the project's features; a new base map redraws them in its colours (decision 654)
  useEffect(() => {
    const m = map.current;
    const g = group.current;
    if (!m || !g) return;
    g.clearLayers();
    const read = elementCss(host.current);
    const shapes = features.map((f) => ({ f, ...shapesOf(f, read) }));
    // every edge under every line, so an edge never crosses a line drawn before it
    for (const s of shapes) for (const u of s.under) g.addLayer(u);
    for (const { f, main: shape } of shapes) {
      shape.bindTooltip(textNode(f.tooltip), { sticky: true });
      if (identify && f.identify.length > 0)
        shape.bindPopup(() => identifyPopup(f.identify), { maxWidth: 320 });
      g.addLayer(shape);
      if (f.label && f.label_at) {
        g.addLayer(
          L.tooltip({ permanent: true, direction: 'right', className: 'map-label' })
            .setLatLng([f.label_at.lat, f.label_at.lng])
            .setContent(textNode(f.label)),
        );
      }
    }
    if (fitKey !== undefined && fittedKey.current === fitKey) return;
    const b = boundsOf(features.flatMap((f) => featurePoints(f)));
    if (b) {
      m.invalidateSize(); // the box may have been laid out after the map was made
      m.fitBounds(
        [
          [b.south, b.west],
          [b.north, b.east],
        ],
        { maxZoom: 14, padding: [16, 16] },
      );
      fitted.current = true;
      if (fitKey !== undefined) fittedKey.current = fitKey;
    } else if (!fitted.current) {
      m.setView([-25, 134], 4); // Australia, until there is something to show
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the fit key is read with the features it came with
  }, [features, base, identify]);

  // a new fit key (another project): fit again to what it shows, its own aircraft when it has nothing else
  useEffect(() => {
    if (fitKey === undefined) return;
    if (fittedKey.current !== fitKey) fitted.current = false;
  }, [fitKey]);

  // ---- hazards (05-025)
  useEffect(() => {
    const l = layers.current;
    if (!l) return;
    l.hazards.clearLayers();
    const green = colourOf('map-line-not-present', elementCss(host.current));
    for (const layer of hazards ?? [])
      for (const p of layer.placemarks)
        l.hazards.addLayer(hazardLayer(p, l.hazardCanvas, 'portal-hazards', green));
  }, [hazards, base]);

  // ---- hazard pins (Teslyn Field's custom pins, docs/specs/map.md §16.12)
  useEffect(() => {
    const l = layers.current;
    if (!l) return;
    drawHazardPins(l.pins, pins ?? [], 'portal-pins');
  }, [pins]);

  // ---- the aircraft and their trails (03-053), followed when asked
  useEffect(() => {
    const m = map.current;
    const l = layers.current;
    if (!m || !l) return;
    l.trails.clearLayers();
    l.aircraft.clearLayers();
    if (!tracking) return;
    drawAircraft(l.trails, l.aircraft, tracking, {
      offsetH: -new Date().getTimezoneOffset() / 60,
      trailPane: 'portal-trails',
      flightLines: tracking.flightLines ?? true,
      // a public aircraft's planned route, asked once per click (R160; only a staff map passes routeOf)
      onPublicClick: (a) => {
        const f = latest.current.routeOf;
        if (f) l.route.open(a, f, tracking.colours);
      },
    });
    const followed = follow ? tracking.aircraft.find((a) => a.id === follow) : undefined;
    if (followed) {
      m.panTo([followed.lat, followed.lng]);
      return;
    }
    // a project with no plan in the cloud yet: show its own aircraft (never fitted to public air traffic)
    const own = tracking.aircraft.filter((a) => a.fleet !== false);
    if (!fitted.current && own.length > 0) {
      const b = boundsOf(own.map((a) => ({ lat: a.lat, lng: a.lng })));
      if (b) {
        m.fitBounds(
          [
            [b.south, b.west],
            [b.north, b.east],
          ],
          { maxZoom: 11, padding: [24, 24] },
        );
        fitted.current = true;
      }
    }
  }, [tracking, follow]);

  // ---- fly to an aircraft (pressing its name in a tracking list)
  useEffect(() => {
    const m = map.current;
    const a = flyTo ? latest.current.tracking?.aircraft.find((x) => x.id === flyTo.id) : null;
    if (!m || !a) return;
    m.flyTo([a.lat, a.lng], Math.max(m.getZoom(), 11));
  }, [flyTo]);

  // the fire layers over the lines (staff maps only): information only, each popup says so
  useEffect(() => {
    const g = top.current;
    if (!g) return;
    g.clearLayers();
    const read = elementCss(host.current);
    const shapes = (overlay ?? []).map((f) => ({ f, ...shapesOf(f, read) }));
    for (const s of shapes) for (const u of s.under) g.addLayer(u);
    for (const { f, main } of shapes) {
      main.bindTooltip(textNode(f.tooltip), { sticky: true });
      main.bindPopup(() => firePopup(f), { maxWidth: 360 });
      g.addLayer(main);
    }
  }, [overlay, base]);

  // the overlay's credits in the map's corner, as the sources ask
  const credited = useRef<readonly string[]>([]);
  useEffect(() => {
    const control = map.current?.attributionControl;
    if (!control) return;
    const next = [...(credits ?? []), ...(tracking?.credits ?? [])];
    for (const c of credited.current) if (!next.includes(c)) control.removeAttribution(c);
    for (const c of next) if (!credited.current.includes(c)) control.addAttribution(c);
    credited.current = next;
  }, [credits, tracking]);

  return (
    <div
      ref={host}
      className={className ? `portal-map ${className}` : 'portal-map'}
      role={identify ? 'region' : 'img'}
      aria-label={label}
      data-basemap={baseMapKind(base)}
    />
  );
}
